import { LitElement, html, css } from 'lit';
import { readPrefsState, writePrefs } from '../../runtime/prefs.js';

// Variant → status token suffix (danger public API → failure token)
const VARIANT_TOKEN = {
  info:    'info',
  success: 'success',
  warning: 'warning',
  danger:  'failure',
  neutral: 'neutral',
};

// Per WAI-ARIA + M3 spec: assertive variants (status changes the user must
// notice immediately) use role="alert"; informational/idle variants use the
// polite role="status".
const VARIANT_ROLE = {
  info:    'status',
  success: 'alert',
  warning: 'alert',
  danger:  'alert',
  neutral: 'status',
};

const VALID_VARIANTS = new Set(Object.keys(VARIANT_TOKEN));

export class CrusherAlert extends LitElement {
  static styles = css`
    :host {
      display: block;
    }
    :host([hidden]) {
      display: none;
    }

    .alert {
      box-sizing: border-box;
      display: grid;
      grid-template-columns: auto 1fr auto;
      align-items: start;
      gap: var(--crusher-spacing-3);
      padding: var(--crusher-spacing-5);
      border-radius: var(--crusher-radius-md);
      border-inline-start: 4px solid var(--alert-accent);
      background: color-mix(in srgb, var(--alert-accent) 12%, var(--crusher-background-surface));
    }

    /* Variant accent token bindings */
    .alert--info    { --alert-accent: var(--crusher-color-status-info); }
    .alert--success { --alert-accent: var(--crusher-color-status-success); }
    .alert--warning { --alert-accent: var(--crusher-color-status-warning); }
    .alert--danger  { --alert-accent: var(--crusher-color-status-failure); }
    .alert--neutral { --alert-accent: var(--crusher-color-status-neutral); }

    .alert__icon {
      display: flex;
      align-items: flex-start;
      padding-top: 2px;
      color: var(--alert-accent);
      flex-shrink: 0;
      font-size: var(--crusher-font-size-base);
      line-height: 1.5;
    }

    .alert__body {
      display: flex;
      flex-direction: column;
      gap: var(--crusher-spacing-2);
      min-width: 0;
    }

    ::slotted([slot="title"]) {
      display: block;
      font-weight: var(--crusher-font-weight-semibold);
      font-size: var(--crusher-font-size-sm);
      color: var(--alert-accent);
      margin: 0;
    }

    .alert__content {
      color: var(--crusher-text-primary);
      font-size: var(--crusher-font-size-sm);
      line-height: var(--crusher-line-height-relaxed, 1.6);
    }

    .alert__actions {
      display: flex;
      flex-wrap: wrap;
      gap: var(--crusher-spacing-2);
    }

    .alert__close {
      appearance: none;
      background: transparent;
      border: none;
      padding: 0;
      margin: 0;
      cursor: var(--crusher-alert-cursor, pointer);
      color: var(--crusher-text-secondary);
      font-size: var(--crusher-font-size-base);
      line-height: 1;
      border-radius: var(--crusher-radius-sm);
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: calc(var(--crusher-spacing-4) + var(--crusher-spacing-2));
      height: calc(var(--crusher-spacing-4) + var(--crusher-spacing-2));
      transition:
        color var(--crusher-motion-duration-fast, 150ms) ease,
        background-color var(--crusher-motion-duration-fast, 150ms) ease;
    }

    .alert__close:hover {
      color: var(--crusher-text-primary);
      background: color-mix(in srgb, var(--crusher-text-primary) 10%, transparent);
    }

    .alert__close:focus-visible {
      outline: var(--crusher-focus-width, 2px) solid var(--crusher-color-brand-primary);
      outline-offset: 2px;
    }

    @media (prefers-reduced-motion: reduce) {
      .alert__close {
        transition: none;
      }
    }
  `;

  static properties = {
    variant:      { type: String, reflect: true },
    dismissible:  { type: Boolean, reflect: true },
    _isDismissed: { type: Boolean, state: true },
  };

  constructor() {
    super();
    this.variant = 'info';
    this.dismissible = false;
    this._isDismissed = false;
  }

  // ---- Lifecycle ----

  connectedCallback() {
    super.connectedCallback();
    if (this.dismissible && this.id) {
      const { prefs } = readPrefsState();
      if (prefs.alertsDismissed && prefs.alertsDismissed[this.id] === true) {
        this._isDismissed = true;
        this.hidden = true;
      }
    }
  }

  // ---- Dismiss handler ----

  _dismiss() {
    if (this.id) {
      writePrefs((prefs) => {
        const dismissed = prefs.alertsDismissed || {};
        dismissed[this.id] = true;
        return { ...prefs, alertsDismissed: dismissed };
      });
    }
    this._isDismissed = true;
    this.hidden = true;
    this.dispatchEvent(new CustomEvent('crusher:alertdismiss', {
      detail: { id: this.id || null },
      bubbles: true,
      composed: true,
    }));
  }

  // ---- Default icons per variant (Lit html`` template, no innerHTML) ----

  _defaultIcon() {
    const v = VALID_VARIANTS.has(this.variant) ? this.variant : 'info';
    if (v === 'info') {
      return html`<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" focusable="false">
        <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 3a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm-.75 3.5h1.5v4h-1.5v-4z"/>
      </svg>`;
    }
    if (v === 'success') {
      return html`<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" focusable="false">
        <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm3.28 5.53-4 4a.75.75 0 0 1-1.06 0l-2-2a.75.75 0 1 1 1.06-1.06l1.47 1.47 3.47-3.47a.75.75 0 1 1 1.06 1.06z"/>
      </svg>`;
    }
    if (v === 'warning') {
      return html`<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" focusable="false">
        <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
      </svg>`;
    }
    if (v === 'danger') {
      return html`<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" focusable="false">
        <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm2.78 4.22a.75.75 0 1 1-1.06 1.06L8 6.06l-1.72 1.72a.75.75 0 0 1-1.06-1.06L6.94 5 5.22 3.28a.75.75 0 0 1 1.06-1.06L8 3.94l1.72-1.72a.75.75 0 1 1 1.06 1.06L9.06 5l1.72 1.72z"/>
      </svg>`;
    }
    // neutral
    return html`<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" focusable="false">
      <circle cx="8" cy="8" r="3"/>
    </svg>`;
  }

  // ---- Render ----

  render() {
    if (this._isDismissed) return html``;

    const v = VALID_VARIANTS.has(this.variant) ? this.variant : 'info';
    const role = VARIANT_ROLE[v];

    const titleEl = this.querySelector('[slot="title"]');
    const titleText = titleEl?.textContent?.trim();
    const dismissLabel = titleText
      ? `Dismiss ${titleText}`
      : (this.id ? `Dismiss alert ${this.id}` : 'Dismiss alert');

    return html`
      <div class="alert alert--${v}" role=${role}>
        <div class="alert__icon">
          <slot name="icon">${this._defaultIcon()}</slot>
        </div>
        <div class="alert__body">
          <slot name="title"></slot>
          <div class="alert__content"><slot></slot></div>
          <div class="alert__actions"><slot name="actions"></slot></div>
        </div>
        ${this.dismissible ? html`
          <button
            type="button"
            class="alert__close"
            aria-label=${dismissLabel}
            @click=${this._dismiss}
          >&#215;</button>
        ` : ''}
      </div>
    `;
  }
}

customElements.define('crusher-alert', CrusherAlert);
